import { useEffect, useState } from 'react';
import { useAuth } from '../../contexts';
import { api, LANCAMENTOS_ALTERADOS_EVENT, type TransactionUsage } from '../../services/api';

/**
 * Uso de lançamentos do plano Gratuito (MAN-364), compartilhado entre a barra do
 * Header e o bloqueio das telas de lançar: uma consulta serve os dois.
 *
 * Reconsulta ao montar (cada troca de tela monta o Header de novo) e quando
 * lançamentos são criados ou apagados (evento disparado pelo `api`).
 */
let ultimo: TransactionUsage | null = null;
let emAndamento: Promise<void> | null = null;
const ouvintes = new Set<(uso: TransactionUsage | null) => void>();

function publicar(uso: TransactionUsage | null) {
    ultimo = uso;
    ouvintes.forEach((ouvinte) => ouvinte(uso));
}

function recarregar(): Promise<void> {
    if (emAndamento) return emAndamento;
    emAndamento = api
        .getTransactionUsage()
        .then((uso) => publicar(uso ?? null))
        // Sem a contagem, a tela segue como antes: sem barra e sem bloqueio. O
        // backend continua recusando o lançamento acima do limite.
        .catch(() => undefined)
        .finally(() => {
            emAndamento = null;
        });
    return emAndamento;
}

export function useTransactionUsage(): TransactionUsage | null {
    const { isAuthenticated, isAdmin } = useAuth();
    const [uso, setUso] = useState<TransactionUsage | null>(ultimo);
    const ativo = isAuthenticated && !isAdmin;

    useEffect(() => {
        if (!ativo) {
            // Saiu da conta (ou é admin): a contagem guardada não vale mais. O
            // retorno do hook já é null nesse caso, sem precisar de setState aqui.
            ultimo = null;
            return;
        }
        ouvintes.add(setUso);
        recarregar();
        const aoAlterar = () => {
            recarregar();
        };
        window.addEventListener(LANCAMENTOS_ALTERADOS_EVENT, aoAlterar);
        return () => {
            ouvintes.delete(setUso);
            window.removeEventListener(LANCAMENTOS_ALTERADOS_EVENT, aoAlterar);
        };
    }, [ativo]);

    return ativo ? uso : null;
}
